/**
 * Name the audit report a visitor arrived from, on the sign-in page itself.
 *
 * 2026-08-20 teardown of the report CTAs. Every sign-up button on an outreach
 * report carries `share=<shareId>` (signupCtaUrl), and since 2026-08-19 that
 * share is redeemed after sign-up by useSiteAuditClaim. But the page in
 * between said only "Sign in or create an account. Enter your email and
 * password to continue." A reader who clicked "Run this on your product" or
 * "Fix these 35 findings with your team" landed on a form that mentioned
 * neither their site nor their findings, so the click delivered none of what
 * the label promised. That gap is identical in both CTA copy arms, which is
 * why closing it is not part of the A/B.
 *
 * Deliberately COSMETIC. It renders a line or it renders nothing; a dead
 * share, a slow network, or an offline visitor must never delay or block
 * signing in. Sibling of useSiteAuditClaim, which does the actual import.
 */
import { useEffect, useState } from 'react';
import { siteAuditsApi } from '@/lib/api/siteAudits';
import { log } from '@/lib/logger';

/**
 * The audited site as the notice prints it. `www.` is stripped to match how
 * claimSiteAudit names the project it creates ("Site audit: <hostname>"), so
 * the promise on the sign-in page and the project the visitor lands in agree.
 * Anything unparseable returns null and the notice does not render, rather
 * than printing junk from a stored URL.
 */
export function auditHostname(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
    return parsed.hostname.replace(/^www\./, '') || null;
  } catch {
    return null;
  }
}

/** Returns the audited hostname once known, or null (no share, or lookup failed). */
export function useSiteAuditPreview(shareId: string | null | undefined): string | null {
  const [hostname, setHostname] = useState<string | null>(null);

  useEffect(() => {
    if (!shareId) return;
    let cancelled = false;

    siteAuditsApi.shareStats(shareId)
      .then((stats) => {
        if (cancelled) return;
        const host = auditHostname(stats?.url);
        log.info('[useSiteAuditPreview] resolved share', { shareId, hostname: host });
        setHostname(host);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Logged with the real reason, not a bare object: this is the only
        // signal that a report link is pointing at a share that no longer
        // exists, which would also mean the post-sign-up claim will 404.
        log.warn('[useSiteAuditPreview] share stats lookup failed', {
          shareId,
          status: (err as { status?: number })?.status,
          message: err instanceof Error ? err.message : String(err),
        });
      });

    return () => { cancelled = true; };
  }, [shareId]);

  return hostname;
}
